import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, DollarSign, Clock, Building2, Package, Gift } from "lucide-react";
import Button from "../../components/ui/Button/Button.jsx";
import Panel from "../../components/ui/Panel/Panel.jsx";
import StatusBadge from "../../components/ui/StatusBadge/StatusBadge.jsx";
import ProgressBar from "../../components/ui/ProgressBar/ProgressBar.jsx";
import EngagementCard from "../../components/dashboard/EngagementCard/EngagementCard.jsx";
import { useApp } from "../../hooks/useApp.js";
import { getToken } from "../../services/api.js";
import { listBusinesses } from "../../services/businessService.js";
import { listFilings } from "../../services/filingsService.js";
import { getReturnById } from "../../services/trucksService.js";
import { getMySubscriptions } from "../../services/subscriptionService.js";
import { getReferralSummary } from "../../services/engagementService.js";
import { goToNewFiling } from "../../utils/newFiling.js";
import { DRAFT_STATUSES, getMergedStatus } from "../../utils/filingStatus.js";
import { getTaxYears } from "../../services/lookupService.js";
import { formatDate, getTaxPeriod } from "../../utils/deadlines.js";
import { money } from "../../utils/format.js";

// Recent-filings snapshot for the dashboard's own table - not a full listing
// (My Filings covers that, with real filters/search). 100 is generous enough
// to cover the YTD stat cards for typical usage without a dedicated backend
// aggregate endpoint; the table itself paginates this client-side, 10/page.
const SNAPSHOT_SIZE = 100;
const PAGE_SIZE = 10;

// Same values/labels as Subscriptions.jsx's own STATUS_STYLES/StatusChip (subscription
// statuses, not the filing statuses StatusBadge/filingStatus.js covers) - kept local here
// too rather than extracted, matching this file's existing pattern of small
// widget-scoped helpers (StatCard right below).
const SUBSCRIPTION_STATUS_STYLES = {
  ACTIVE: { label: "Active", bg: "var(--accent-soft)", color: "var(--accent)" },
  EXHAUSTED: { label: "Exhausted", bg: "var(--panel-2)", color: "var(--muted)" },
  CANCELLED: { label: "Cancelled", bg: "var(--danger-soft)", color: "var(--danger)" },
  EXPIRED: { label: "Expired", bg: "var(--panel-2)", color: "var(--muted)" },
  PENDING: { label: "Pending", bg: "var(--amber-soft)", color: "var(--amber)" },
};

function SubscriptionStatusChip({ status }) {
  const s = SUBSCRIPTION_STATUS_STYLES[status] || { label: status || "—", bg: "var(--panel-2)", color: "var(--muted)" };
  return (
    <span
      className="inline-flex items-center gap-1 px-[8px] py-[3px] rounded-[20px] text-[11px] font-semibold whitespace-nowrap flex-shrink-0"
      style={{ background: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
}

function StatCard({ icon, label, value, sub, valueClassName = "text-[22px]" }) {
  return (
    <Panel className="relative overflow-hidden h-full px-4 py-3.5">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-[8px] bg-[var(--accent-soft)] text-[var(--accent)] grid place-items-center flex-none">
          {icon}
        </div>
        <div className="text-[11px] font-semibold text-[var(--muted)] uppercase tracking-[0.06em] truncate">{label}</div>
      </div>
      <div className={`${valueClassName} font-bold text-[var(--ink)] tracking-[-0.02em] leading-none`}>{value}</div>
      {sub && <div className="text-[12px] text-[var(--muted)] mt-[5px]">{sub}</div>}
    </Panel>
  );
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { state, dispatch, showToast } = useApp();
  const [businesses, setBusinesses] = useState([]);
  const [filings, setFilings] = useState([]);
  const [totalFilings, setTotalFilings] = useState(0);
  const [subscriptions, setSubscriptions] = useState([]);
  const [taxYears, setTaxYears] = useState([]);
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [openingId, setOpeningId] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    Promise.all([
      listBusinesses(),
      listFilings({ page: 0, size: SNAPSHOT_SIZE }),
      getMySubscriptions().catch(() => []),
      // Tax period lookup for the Tax Period card. A failure here only blanks that
      // card (it shows "—"), never the rest of the dashboard.
      getTaxYears().catch(() => []),
      // Referral credit for the Referral Credit card. Same as Tax Period: a failure only
      // blanks that card (it shows "—").
      getReferralSummary().catch(() => null),
    ])
      .then(([bizList, filingsRes, subs, years, ref]) => {
        if (cancelled) return;
        setBusinesses(bizList);
        setFilings(filingsRes.rows);
        setTotalFilings(filingsRes.totalElements);
        setSubscriptions(subs || []);
        setTaxYears(years || []);
        setReferral(ref);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // This page's own empty state (below) already keeps this button from ever
  // rendering with zero businesses, but goToNewFiling is used here too so
  // the guard doesn't silently drift out of sync if that empty state ever
  // changes - see Sidebar.jsx's startNewFiling for the primary entry point.
  const startNew = () => {
    goToNewFiling({ dispatch, navigate, showToast });
  };

  // Same pattern as MyFilings.jsx's openFiling/viewFiling - resumes a draft
  // in the wizard (loading this exact return_id's saved data via
  // getReturnById, rather than letting Wizard.jsx's own fetch effect guess
  // at "whatever this business's current draft is"), or opens a
  // submitted/accepted/rejected return read-only on the Confirmation screen.
  const openFiling = async (row) => {
    setOpeningId(row.id);
    try {
      const r = await getReturnById(row.id);
      dispatch({ type: "SEED", which: "new" });
      dispatch({ type: "SET_BIZ", patch: { name: row.businessName, ein: row.ein } });
      dispatch({ type: "SET_BUSINESS_ID", businessId: row.businessId });
      dispatch({ type: "SET_RETURN", ...r });
      dispatch({ type: "GO_STEP", step: 2 });
      navigate("/wizard/1");
    } catch (err) {
      showToast(err.message || "Couldn't open this filing. Please try again.");
    } finally {
      setOpeningId(null);
    }
  };

  const viewFiling = (row) => {
    dispatch({ type: "SET_BIZ", patch: { name: row.businessName, ein: row.ein } });
    dispatch({ type: "SET_BUSINESS_ID", businessId: row.businessId });
    dispatch({
      type: "SET_RETURN",
      returnId: row.id,
      year: row.year,
      month: state.month,
      trucks: [],
      totals: { line6: row.balanceDue },
      perCategory: [],
    });
    dispatch({
      type: "FILE",
      result: { returnId: row.id, status: getMergedStatus(row), submissionId: row.submissionId },
    });
    navigate("/confirm");
  };

  // Tax Period card: the current July-June period.
  const taxPeriod = useMemo(() => getTaxPeriod(taxYears), [taxYears]);

  const stats = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const acceptedThisYear = filings.filter((f) => {
      if (getMergedStatus(f) !== "accepted") return false;
      const created = f.createdAt ? new Date(f.createdAt) : null;
      return created ? created.getFullYear() === currentYear : false;
    });
    const balanceDueYtd = acceptedThisYear.reduce((sum, f) => sum + (Number(f.balanceDue) || 0), 0);
    const draftCount = filings.filter((f) => DRAFT_STATUSES.has(f.status)).length;
    return { balanceDueYtd, draftCount, acceptedCount: acceptedThisYear.length };
  }, [filings]);

  const totalPages = Math.max(1, Math.ceil(filings.length / PAGE_SIZE));
  const pageRows = filings.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const hasBusinesses = businesses.length > 0;

  if (!loading && !hasBusinesses) {
    return (
      <div className="max-w-[1180px] mx-auto px-[22px] py-[26px] pb-[70px]">
        <div className="mb-7">
          <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
            Your returns
          </div>
          <h1 className="font-disp font-bold text-[32px] leading-[1.1] mt-1 text-[var(--ink)]">Dashboard</h1>
          <p className="text-[var(--muted)] max-w-[62ch] mt-1">
            Pick up where you left off, or start a new Form 2290.
          </p>
        </div>
        <Panel padded className="text-center py-10">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[var(--accent-soft)] text-[var(--accent)] grid place-items-center text-3xl mb-3">
            +
          </div>
          <h2 className="text-xl font-semibold mb-1">Welcome to EasyFile2290</h2>
          <p className="text-[var(--muted)] max-w-[46ch] mx-auto mb-4">
            You haven't added a business yet. Add one to start filing your Form 2290.
          </p>
          <Button
            size="lg"
            onClick={() => {
              dispatch({ type: "SEED", which: "new" });
              navigate("/dashboard/businesses/form", { state: { returnTo: "/wizard/1" } });
            }}
          >
            Add Business
          </Button>
        </Panel>
      </div>
    );
  }

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[26px] pb-[70px]">
      <div className="flex justify-between items-end mb-6 gap-4 flex-wrap">
        <div>
          <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
            Your returns
          </div>
          <h1 className="font-disp font-bold text-[32px] leading-[1.1] mt-1 text-[var(--ink)]">Dashboard</h1>
          <p className="text-[var(--muted)] max-w-[62ch] mt-1">
            Pick up where you left off, or start a new Form 2290.
          </p>
        </div>
        <Button size="lg" onClick={startNew}>
          + New Filing
        </Button>
      </div>

      {!loading && (
        <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" }}>
          <StatCard
            icon={<FileText size={15} />}
            label="Total Filings"
            value={totalFilings}
            sub={`${stats.acceptedCount} accepted this year · ${stats.draftCount} draft${stats.draftCount !== 1 ? "s" : ""}`}
          />
          <StatCard
            icon={<DollarSign size={15} />}
            // The tax reported on accepted returns, not money still owed: the IRS never tells
            // us when it has been paid (EFW is auto-debited; EFTPS/card are paid on IRS sites),
            // so a "balance due" here would stay on the dashboard after the filer has paid.
            label={`Tax Filed (${new Date().getFullYear()})`}
            value={stats.balanceDueYtd > 0 ? money(stats.balanceDueYtd) : "—"}
            sub={`${stats.acceptedCount} accepted return${stats.acceptedCount !== 1 ? "s" : ""} · paid directly to the IRS`}
          />
          <StatCard
            icon={<Clock size={15} />}
            label="Tax Period"
            value={
              taxPeriod ? (
                // Each date stays on one line, so a narrow card wraps at the dash.
                <>
                  <span className="whitespace-nowrap">{formatDate(taxPeriod.start)}</span> –{" "}
                  <span className="whitespace-nowrap">{formatDate(taxPeriod.end)}</span>
                </>
              ) : (
                "—"
              )
            }
            valueClassName="text-[14px] leading-snug"
          />
          <StatCard icon={<Building2 size={15} />} label="Businesses" value={businesses.length} sub="Registered on your account" />
          {/* The whole card opens the Referral Credit screen. */}
          <Link to="/dashboard/referrals" className="block no-underline rounded-[var(--r)]">
            <StatCard
              icon={<Gift size={15} />}
              label="Referral Credit"
              value={referral ? money(referral.availableAmount) : "—"}
              sub={
                <>
                  {referral ? `${referral.earned} earned · ` : ""}
                  <span className="text-[var(--accent)] font-semibold">View referrals →</span>
                </>
              }
            />
          </Link>
        </div>
      )}

      {!loading && subscriptions.length > 0 && (
        <Panel padded className="mb-6">
          <div className="flex items-center justify-between mb-3.5 flex-wrap gap-2">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-[10px] bg-[var(--accent-soft)] text-[var(--accent)] grid place-items-center flex-shrink-0">
                <Package size={17} />
              </div>
              <div>
                <div className="text-[13px] font-bold text-[var(--ink)]">Subscription usage</div>
                <div className="text-[12px] text-[var(--muted)]">Return credits from your active packages</div>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="!text-xs !px-3 !py-1.5" onClick={() => navigate("/dashboard/subscriptions")}>
              Buy more returns →
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {subscriptions.map((s) => {
              const low = s.returnsRemaining <= Math.max(1, Math.round(s.totalReturnsPurchased * 0.2));
              return (
                <div key={s.id} className="border border-[var(--line)] rounded-[var(--r-sm)] p-3.5">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-[13px] font-bold text-[var(--ink)]">
                      {s.categoryName} — {s.packageQuantity === 1 ? "1 unit" : `${s.packageQuantity} units`}
                    </div>
                    <SubscriptionStatusChip status={s.subscriptionStatus} />
                  </div>
                  <div className="text-[13px] text-[var(--muted)] mt-1 mb-2">
                    {s.returnsUsed} of {s.totalReturnsPurchased} returns used
                  </div>
                  <ProgressBar value={s.returnsUsed} max={s.totalReturnsPurchased} tone={low ? "warn" : "accent"} />
                  <div className="text-[12px] text-[var(--muted)] mt-1.5">
                    <strong className="text-[var(--ink)]">{s.returnsRemaining}</strong> return
                    {s.returnsRemaining === 1 ? "" : "s"} remaining
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* Rate-your-filing prompt (when one is due) + the always-on share card, see EngagementCard. */}
      {getToken() && <EngagementCard />}

      <Panel className="overflow-hidden">
        <div className="px-5 pt-[18px] pb-3 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[15px] font-bold text-[var(--ink)]">Filing history</div>
            <div className="text-[13px] text-[var(--muted)] mt-0.5">Your most recent Form 2290 returns</div>
          </div>
          <Button variant="ghost" size="sm" className="!text-xs !px-3 !py-1.5" onClick={() => navigate("/dashboard/filings")}>
            View all →
          </Button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[var(--muted)]">Loading…</div>
        ) : filings.length === 0 ? (
          <div className="p-10 text-center text-[var(--muted)]">No filings yet — start your first one above.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[var(--line)]">
                    {["Business", "Tax Year", "Status", ""].map((h) => (
                      <th
                        key={h}
                        className="text-[12px] font-semibold text-[var(--muted)] uppercase tracking-[0.06em] py-3 px-4 text-left bg-[var(--panel-2)] whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row) => {
                    const isDraft = DRAFT_STATUSES.has(row.status);
                    const mergedStatus = getMergedStatus(row);
                    // A rejected return isn't a draft server-side, but the
                    // useful action on it is fixing the error and
                    // resubmitting, not a read-only look - same as My
                    // Filings, so it gets Continue here too, not View.
                    const isRejected = mergedStatus === "rejected";
                    return (
                      <tr key={row.id} className="border-b border-[var(--line)] last:border-b-0 hover:bg-[var(--accent-soft)] transition-colors">
                        <td className="py-3.5 px-4 text-[14px] font-semibold text-[var(--ink)]">
                          {row.businessName || "—"}
                          {row.ein && <div className="text-[12px] font-normal text-[var(--muted)] font-mono">{row.ein}</div>}
                        </td>
                        <td className="py-3.5 px-4 text-[14px] text-[var(--muted)]">{row.year || "—"}</td>
                        <td className="py-3.5 px-4">
                          <StatusBadge status={mergedStatus} />
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          {isDraft || isRejected ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="!text-xs !px-3 !py-1.5"
                              onClick={() => openFiling(row)}
                              disabled={openingId === row.id}
                            >
                              {openingId === row.id ? "…" : isRejected ? "Fix & Resubmit →" : "Continue →"}
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" className="!text-xs !px-3 !py-1.5" onClick={() => viewFiling(row)}>
                              View →
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-[var(--line)]">
                <div className="text-[13px] text-[var(--muted)]">
                  Page {page + 1} of {totalPages}
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="!text-xs !px-3 !py-1.5"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    ← Previous
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="!text-xs !px-3 !py-1.5"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Next →
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Panel>
    </div>
  );
};

export default Dashboard;
